import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  disconnectGoogleCalendar,
  fetchGoogleCalendarStatus,
  startGoogleCalendarConnection,
} from '../../api/integrations';
import { useToast } from '../tavern/ToastProvider';

const GoogleCalendarPanel = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await fetchGoogleCalendarStatus());
    } catch {
      toast.error(t('integrations.googleCalendar.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const result = searchParams.get('googleCalendar');
    if (!result) return;

    if (result === 'connected') {
      toast.success(t('integrations.googleCalendar.connectSuccess'));
      loadStatus();
    } else if (result === 'error') {
      toast.error(t('integrations.googleCalendar.connectError'));
    }

    searchParams.delete('googleCalendar');
    const nextSearch = searchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, {
      replace: true,
      state: {},
    });
  }, [loadStatus, location.pathname, navigate, searchParams, t, toast]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await startGoogleCalendarConnection('/settings');
      if (response?.authorizationUrl) {
        window.location.assign(response.authorizationUrl);
        return;
      }
      throw new Error('Missing authorization URL');
    } catch {
      toast.error(t('integrations.googleCalendar.connectError'));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectGoogleCalendar();
      toast.success(t('integrations.googleCalendar.disconnectSuccess'));
      await loadStatus();
    } catch {
      toast.error(t('integrations.googleCalendar.disconnectError'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={20} />
        <Typography color="text.secondary">{t('integrations.googleCalendar.loading')}</Typography>
      </Stack>
    );
  }

  if (!status?.configured) {
    return (
      <Alert severity="info">
        {t('integrations.googleCalendar.unavailable')}
      </Alert>
    );
  }

  const connection = status.connection;
  const needsReconnect = connection?.status === 'needs_reconnect';
  const connected = status.connected && !needsReconnect;

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(15, 76, 69, 0.1)',
          color: 'primary.main',
        }}
      >
        <EventAvailableOutlinedIcon />
      </Box>

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="h3">{t('integrations.googleCalendar.title')}</Typography>
          {connected ? (
            <Chip size="small" color="success" label={t('integrations.googleCalendar.connected')} />
          ) : needsReconnect ? (
            <Chip size="small" color="warning" label={t('integrations.googleCalendar.needsReconnect')} />
          ) : null}
        </Stack>
        <Typography color="text.secondary">
          {t('integrations.googleCalendar.copy')}
        </Typography>
      </Box>

      {connected ? (
        <Alert severity="success" sx={{ width: '100%' }}>
          {t('integrations.googleCalendar.connectedAs', {
            email: connection?.googleEmail || t('integrations.googleCalendar.connectedAccount'),
          })}
        </Alert>
      ) : needsReconnect ? (
        <Alert severity="warning" sx={{ width: '100%' }}>
          {t('integrations.googleCalendar.reconnectCopy')}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ width: '100%' }}>
          {t('integrations.googleCalendar.notConnected')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
        <Button
          variant="contained"
          startIcon={connecting ? <CircularProgress color="inherit" size={18} /> : <SyncOutlinedIcon />}
          onClick={handleConnect}
          disabled={connecting || disconnecting}
          sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
        >
          {needsReconnect
            ? t('integrations.googleCalendar.reconnect')
            : connected
              ? t('integrations.googleCalendar.reconnect')
              : t('integrations.googleCalendar.connect')}
        </Button>
        {connected || needsReconnect ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={
              disconnecting ? <CircularProgress color="inherit" size={18} /> : <LinkOffOutlinedIcon />
            }
            onClick={handleDisconnect}
            disabled={connecting || disconnecting}
          >
            {t('integrations.googleCalendar.disconnect')}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default GoogleCalendarPanel;
